import {
	Box,
	Button,
	Checkbox,
	Chip,
	Dialog, 
    DialogTitle, 
    DialogContent, 
    DialogContentText, 
    DialogActions,    
	FormControl,
    Divider,
	FormControlLabel,
	FormGroup,
	Menu, 
    MenuItem,
	Radio,
	RadioGroup,
	Select,
	Stack,
	TextField,
	Typography,
	useTheme,
    Autocomplete,
    Popper,
    Tooltip,
} from "@mui/material";
import {Link, useNavigate,useSearchParams, useParams} from "react-router-dom";
import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ApiRequestFailed, authenticateAndFetchData, authenticateAndPostData, retryFn } from "@lib/apis";
import PageLoading from "@components/PageLoading";
import PageDataErrorHandler from "@components/PageDataErrorHandler";
import { ColDef, ValueFormatterParams } from "ag-grid-community";
import { CustomCellRendererProps } from "ag-grid-react";
import { Add, CloudUpload, Delete, Downloading, ArrowBack, Edit, MoreVert, Update } from "@mui/icons-material";
import PaginatedServerSideDataTable from "@components/PaginatedServerSideDataTable";
import { styled } from "@mui/material/styles";
import Papa from "papaparse";
import { useSnackbar } from "notistack";
import { urls } from "@routes";
import { formatInTimeZone } from "date-fns-tz";
import { useDialogs } from "@toolpad/core";
import IconButton from "@mui/material/IconButton";
import AdminFieldMapping, { FieldMappingType } from "@components/AdminFieldMapping";
import { format } from "date-fns";
import { exportToCSV, CSVColumn } from "@components/ExportToCSV";
import { getExportFilename } from "@lib/utils";
import {useAuth} from "./AuthProvider";

type ContactListStatus = "uploading" | "verifying" | "active" | "failed" | "deleting" | "pending";

interface ContactList {
	uid: string
	name: string
	created_on: number
	status: ContactListStatus
	contacts_count: number
	column_count: number
	source: string
	available_columns: string[]
    workspace_id: number
    workspace_name: string
    has_unsubscribe_column: boolean
    email_verification_status: string | null
    processed_contacts_count: number
	is_deletable: boolean
	blocking_campaign_name: string
}

interface WorkspaceDetail {
	id: number;
	name: string;
}

interface WebhookMessage {
	event_type: string
	event_data: any
}

interface PaginationData<T> {
	data: T[]
	total_count: number
	total_pages: number
	current_page: number
	page_size: number
	user_id?: number
	user_email?: string
	workspace_details?: WorkspaceDetail[]
    enable_contact_verification: boolean
}

export default function AdminUserContactList() {
    const {userId} = useParams();
    const { user } = useAuth();
    const navigate = useNavigate();
    const theme = useTheme();
    const {enqueueSnackbar} = useSnackbar();
    const dialogs = useDialogs();
    const [searchParams] = useSearchParams();
    const ws = useRef<WebSocket | null>(null);

    const [pageData, setPageData] = useState<PaginationData<ContactList>>();
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);
    const [search, setSearch] = useState("");
    const [contactLists, setContactLists] = useState<ContactList[]>([]);
    const [checkContactListId, setCheckContactListId] = useState("")
    const [openColumnSelectionDialog, setOpenColumnSelectionDialog] = useState(false)
    const [availableColumns, setAvailableColumns] = useState<string[]>([])
    const [updateContactModal, setUpdateContactModal] = useState(false)
    const [updateContactUID, setUpdateContactUID] = useState("")
    const [listName, setListName] = useState("")
    const [taskId, setTaskId] = useState("")
    const [openCsvImportDialog, setOpenCsvImportDialog] = useState<boolean>(
        searchParams.get("newimport") === "1"  // We'll open import dialog on page load if url param is set.
    );
    const [isAdmin, setIsAdmin] = useState(false)
    const [timezone, setTimezone] = useState("")
    const [openFieldMappingDialog, setOpenFieldMappingDialog] = useState<boolean>(false);
    const [selectedContactListForMapping, setSelectedContactListForMapping] = useState<string>("");
    const [workspace, setWorkspaces] = useState<WorkspaceDetail | null>(null);
    const [openAddUpdatePost, setOpenAddUpdatePost] = useState(false)
    const [exportLoading, setExportLoading] = useState(false);

    // Query to fetch page data.
    const pageDataQuery = useQuery({
        queryKey: [
            "contactLists",
            userId,
            page,
            pageSize,
            search
        ],
        queryFn: () =>
            authenticateAndFetchData(
                `/admin/admin-get-user-contact-list/?user_id=${userId}&page=${page}&page_size=${pageSize}&search=${search}`
            ),
        gcTime: 0,
        retry: retryFn,
        refetchOnWindowFocus: false,
    });

    useEffect(() => {
        if (pageDataQuery.data?.data) {
            const response = pageDataQuery.data.data;

            setPageData({
                data: response.data || [],
                total_count: response.total_count,
                total_pages: response.total_pages,
                current_page: response.current_page,
                page_size: response.page_size,
                user_id: response.user_id,
                user_email: response.user_email,
                workspace_details: response.workspace_details,
                enable_contact_verification: response.enable_contact_verification,
            });

            setContactLists(response.data || []);
        }
    }, [pageDataQuery.data]);

    const CheckTasKProgressMutation = useMutation({
            mutationKey: ["CheckTasKProgressMutation"],
            mutationFn: () => authenticateAndPostData("/get-celery-task-progress/", {
                task_id: taskId,
                contact_id: checkContactListId
            }),
            gcTime: 0,
            retry: retryFn,
        })

        useEffect(() => {
            if (taskId) {
                pollTaskProgress();
            }
        }, [taskId]);

    useEffect(() => {
            const wsUrl = `${process.env.REACT_APP_WS_HOST}/ws/contact-list/${userId}/`;

            // Initialize WebSocket connection
            if (userId) {
                ws.current = new WebSocket(wsUrl);

                ws.current.onopen = () => {
                    console.log("WebSocket connection established");
                };

                ws.current.onmessage = (event) => {
                    const data: WebhookMessage = JSON.parse(event.data)["event_data"];
                    const eventName: string = data["event_name"];
                    const eventData: any = data["event_data"];

                    if (eventName === "contact_list_status_update") {
                        const contactListUID: string = eventData["contact_list_uid"];

                        setContactLists(prevItems => prevItems.map(contactList => {
                            return contactList.uid === contactListUID ? {
                                ...contactList,
                                ...(eventData["new_status"] !== undefined ? {status: eventData["new_status"]} : {}),
                                ...(eventData["total_contacts"] !== undefined ? {contacts_count: eventData["total_contacts"]} : {}),
                                ...(eventData["total_columns"] !== undefined ? {column_count: eventData["total_columns"]} : {}),
                                ...(eventData["email_verification_status"] !== undefined ? {email_verification_status: eventData["email_verification_status"]} : {}),
                            } : contactList
                        }));

                        pageDataQuery.refetch().then();
                    } else {
                        console.log("unhandled ws event:", event);
                    }
                }

                ws.current.onclose = () => {
                    console.log("WebSocket connection closed");
                };

                ws.current.onerror = (error) => {
                    console.error("WebSocket error:", error);
                };
            }
        }, [userId]);

        const pollTaskProgress = () => {
            const interval = setInterval(() => {
                CheckTasKProgressMutation.mutate(undefined, {
                    onSuccess: (res) => {
                        const status = res.data.success;
                        if (!status) {
                            if (res.data.status == "pending") {
                                setOpenColumnSelectionDialog(true)
                                setAvailableColumns(res.data.available_columns)
                            }
                            clearInterval(interval);
                            setTaskId("")
                        }
                    },
                    onError: (err) => {
                        console.error("Error fetching task progress:", err);
                        clearInterval(interval);
                        setTaskId("")
                    },
                });
            }, 2000);
        };

    const deleteContactListMutation = useMutation({
            mutationKey: ["deleteContactListMutation"],
            mutationFn: ({contact_list_uid, workpsaceId} : {contact_list_uid: string, workpsaceId: string}) => authenticateAndPostData(`/admin/admin-delete-contact-list/?user_id=${userId}`, {
                contact_list_uid: contact_list_uid,
                workspace_id: workpsaceId,
            }),
            gcTime: 0,
            retry: retryFn,
            onSuccess: () => {
                enqueueSnackbar("Contact list deleted successfully.", {
                    variant: "success",
                });
                pageDataQuery.refetch().then();
            },
            onError: (error: ApiRequestFailed) => {
                console.error(error);
                enqueueSnackbar(error.data.message, {
                    variant: "error",
                });
            }
        })


    // const updateContactListNameMutation = useMutation({
    //         mutationKey: ["updateContactListName"],
    //         mutationFn: ({ uid, name }: { uid: string; name: string }) =>
    //             authenticateAndPostData("/update-contact-listname/", {
    //                 contact_list_id: uid,
    //                 name: name,
    //             }),
    //         gcTime: 0,
    //         retry: retryFn,
    //         onSuccess: (_, variables) => {
    //             setContactLists((prev) =>
    //                 prev.map((list) =>
    //                     list.uid === variables.uid ? { ...list, name: variables.name } : list
    //                 )
    //             );
    //             enqueueSnackbar("Contact list renamed successfully!", {
    //                 variant: "success",
    //             });
    //         },
    //         onError: (error: ApiRequestFailed) => {
    //             console.error(error);
    //             enqueueSnackbar(error.data.message, {
    //                 variant: "error",
    //             });
    //         },
    //     });

    const handlePageChange = (newPage: number) => {
        setPage(newPage);
    };

    const handlePageSizeChange = (newPageSize: number) => {
        setPageSize(newPageSize);
        setPage(1);
    };

    const handleSearchChange = (value: string) => {
        setSearch(value);
        setPage(1);
    };

    // Table functions
    function timestampFormatter(params: ValueFormatterParams) {
        if (params.value !== null) {
            return format(new Date(params.value), "do MMM y, HH:mm:ss (xxx)");
        } else {
            return "---"
        }
    }

    function closeCsvImportDialog() {
		setOpenCsvImportDialog(false);
	}

	/**
	 * Call this function when csv file import API call has completed successfully.
	 */
	function csvContactListImportSuccessful() {
		pageDataQuery.refetch().then();
		setOpenCsvImportDialog(false);
	}

    const renderStatusBadge = (status: string, verificationEnabled: boolean) => {
        switch (status) {
            case "uploading":
                return (
                    <Chip 
                        size="small" 
                        variant="outlined"
                        sx={{ bgcolor: 'rgba(217, 119, 6, 0.1)', color: '#d97706', borderColor: 'transparent', borderRadius: '9999px' }} 
                        label="Uploading..." 
                    />
                );
            case "verifying":
                if (!verificationEnabled) return null;
                return (
                    <Chip 
                        size="small" 
                        variant="outlined"
                        sx={{ bgcolor: 'rgba(37, 99, 235, 0.1)', color: '#2563eb', borderColor: 'transparent', borderRadius: '9999px' }} 
                        label="Verifying..." 
                    />
                );
            case "synchronizing":
                return (
                    <Chip 
                        size="small" 
                        variant="outlined"
                        sx={{ bgcolor: 'rgba(217, 119, 6, 0.1)', color: '#d97706', borderColor: 'transparent', borderRadius: '9999px' }} 
                        label="Synchronizing..." 
                    />
                );
            case "pending":
                return (
                    <Chip 
                        size="small" 
                        variant="outlined"
                        sx={{ bgcolor: 'rgba(217, 119, 6, 0.1)', color: '#d97706', borderColor: 'transparent', borderRadius: '9999px' }} 
                        label="Pending" 
                    />
                );
            case "failed":
                return (
                    <Chip 
                        size="small" 
                        variant="outlined"
                        sx={{ bgcolor: 'rgba(220, 38, 38, 0.1)', color: '#dc2626', borderColor: 'transparent', borderRadius: '9999px' }} 
                        label="Failed" 
                    />
                );
            case "active":
                return null;
            default:
                return (
                    <Chip 
                        size="small" 
                        variant="outlined"
                        sx={{ bgcolor: 'rgba(156, 163, 175, 0.1)', color: '#9ca3af', borderColor: 'transparent', borderRadius: '9999px' }} 
                        label="N/A" 
                    />
                );
        }
    };

    const ContactListDetailsLink = (customProps: any) => {
        const status = customProps.data["status"];
        const enableContactVerification =
            customProps.context?.enableContactVerification ?? true;

        return (
            <Box display="flex" alignItems="center" gap={1}>
                <Link
                    to={urls["adminUserContactListDetails"]
                        .replace(":userId", userId || "")
                        .replace(":contactListUID", customProps.data["uid"])}
                    state={{
                        from: "AdminContactList",
                        userId: userId,
                    }}
                    style={{
                        color: theme.palette.text.primary,
                        textDecoration: "underline",
                    }}
                >
                    {customProps.data["name"]}
                </Link>

                {renderStatusBadge(status, enableContactVerification)}
            </Box>
        );
    };

    const EmailVerificationStatusCell = (customProps: any) => {
        const status = customProps.value;
        const processed: number = customProps.data?.processed_contacts_count ?? 0;
        const total: number = customProps.data?.contacts_count ?? 0;

        if (!status || status === "not_started") {
            return <Chip size="small" variant="filled" color="secondary" sx={{ borderRadius: '9999px' }} label="Not Verified" />;
        }

        if (status === "queued") {
            return (
                <Chip
                    size="small"
                    variant="outlined"
                    sx={{ bgcolor: 'rgba(245, 158, 11, 0.1)', color: '#d97706', borderColor: 'transparent', borderRadius: '9999px' }}
                    label="Queued"
                />
            );
        }

        if (status === "verifying" || status === "processing") {
            return (
                <Chip
                    size="small"
                    variant="outlined"
                    sx={{ bgcolor: 'rgba(37, 99, 235, 0.1)', color: '#2563eb', borderColor: 'transparent', borderRadius: '9999px' }}
                    label={`Verifying… ${processed.toLocaleString()}/${total.toLocaleString()}`}
                />
            );
        }


        if (status === "completed") {
            return (
                <Chip 
                    size="small" 
                    variant="outlined"
                    sx={{ bgcolor: 'rgba(22, 163, 74, 0.1)', color: '#16a34a', borderColor: 'transparent', borderRadius: '9999px' }} 
                    label="Verified" 
                />
            );
        }

        if (status === "failed") {
            return (
                <Chip 
                    size="small" 
                    variant="outlined"
                    sx={{ bgcolor: 'rgba(220, 38, 38, 0.1)', color: '#dc2626', borderColor: 'transparent', borderRadius: '9999px' }} 
                    label="Failed" 
                />
            );
        }

        return <Chip size="small" variant="outlined" sx={{ borderRadius: '9999px' }} label={status} />;
    };

    const StatusBadgeCell = (customProps: CustomCellRendererProps) => {
        if (customProps.value === "uploading") {
            return <Chip size={"small"} color={"default"} label={"Uploading..."} />

        } else if (customProps.value === "active") {
            return <Chip size={"small"} color={"success"} label={"Active"} />

        } else if (customProps.value === "failed") {
            return <Chip size={"small"} color={"error"} label={"Failed"} />

        } else if (customProps.value === "deleting") {
            return <Chip size={"small"} color={"error"} label={"Deleting..."} />

        } else if (customProps.value === "pending") {
            return <Chip size={"small"} color={"warning"} label={"Pending"} />
        } else {
            return <Chip size={"small"} color={"secondary"} label={"N/A"} />
        }
    }
    
    const ActionsButton = (props: CustomCellRendererProps) => {
        const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
        const open = Boolean(anchorEl);
        
        // Controlled state to pass downstream into your modern custom <DeleteContactList /> component setup
        const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

        const handleClick = (event: React.MouseEvent<HTMLElement>) => {
            setAnchorEl(event.currentTarget);
        };

        const handleClose = () => {
            setAnchorEl(null);
        };

        // Extract dynamic keys seamlessly directly from your AG-Grid payload context wrapper
        const { 
            uid, 
            name, 
            is_deletable, 
            blocking_campaign_name 
        } = props.data;

        const handleConfirmDeleteExecution = () => {
            setContactLists((prevState) => {
                return prevState.map((value) => {
                    if (value.uid === uid) {
                        return { ...value, status: "deleting" };
                    }
                    return value;
                });
            });
            deleteContactListMutation.mutate(uid);
            setOpenDeleteDialog(false);
        };

        return (
            <>
                <IconButton
                    aria-label="more"
                    aria-controls={open ? "long-menu" : undefined}
                    aria-expanded={open ? "true" : undefined}
                    aria-haspopup="true"
                    onClick={handleClick}
                    disabled={deleteContactListMutation.isPending}
                >
                    <MoreVert />
                </IconButton>
                
                <Menu
                    id="long-menu"
                    MenuListProps={{
                        "aria-labelledby": "long-button",
                    }}
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                >
                    {/* CRITICAL FIX FOR MUI: Wrapping the disabled item inside a <span> 
                    enables the Tooltip to catch mouse hover triggers correctly.
                    */}
                    <Tooltip
                        title={
                            !is_deletable 
                                ? `This list is currently in use by the campaign "${blocking_campaign_name}" and cannot be deleted.` 
                                : ""
                        }
                        placement="left"
                        arrow
                        disableHoverListener={is_deletable} // Disables the tooltip entirely if item can be safely deleted
                    >
                        <span>
                            <MenuItem 
                                disabled={!is_deletable || deleteContactListMutation.isPending}
                                onClick={() => {
                                    if (is_deletable) {
                                        setOpenDeleteDialog(true);
                                        handleClose();
                                    }
                                }}
                                sx={{
                                    // Emulate the distinct structural styling look and feel of the user dashboard
                                    "&.Mui-disabled": {
                                        opacity: 0.5,
                                        cursor: "not-allowed",
                                        pointerEvents: "auto" // Forces mouse events to propagate cleanly upstream to the Tooltip layer
                                    }
                                }}
                            >
                                <Delete color={is_deletable ? "error" : "disabled"} sx={{ mr: 1 }} />
                                <Typography variant="subtitle2">Delete Contact List</Typography>
                            </MenuItem>
                        </span>
                    </Tooltip>
                </Menu>

                {/* Custom Modal handles background overlay triggers seamlessly */}
               <DeleteContactList 
                    open={openDeleteDialog}
                    close={() => setOpenDeleteDialog(false)}
                    contactName={name}
                    onConfirm={handleConfirmDeleteExecution}
                    isPending={deleteContactListMutation.isPending}
                />
            </>
        );
    };

    const contactListColumns: CSVColumn<any>[] = [
        { header: "List UID", accessor: "uid" },
        { header: "List Name", accessor: "name" },
        { header: "Workspace ID", accessor: "workspace_id" },
        { header: "Workspace Name", accessor: "workspace_name" },
        { header: "Status", accessor: "status" },
        { header: "Contacts Count", accessor: "contacts_count" },
        { header: "Processed Contacts", accessor: "processed_contacts_count" },
        { header: "Verification Status", accessor: "email_verification_status" },
        { header: "Column Count", accessor: "column_count" },
        { header: "Source", accessor: "source" },
        { 
            header: "Created On", 
            accessor: (row) => (row.created_on ? new Date(row.created_on).toLocaleString() : "N/A") 
        },
    ];

    const ExportContactListsCsvButton = () => {        

        const handleExportContactLists = async () => {
            setExportLoading(true);

            try {
                const response = await authenticateAndFetchData(
                    `/admin/admin-get-user-contact-list/?user_id=${userId}&export_all=true`
                );

                const listsToExport = response?.data?.data || [];

                if (!listsToExport.length) {
                    enqueueSnackbar("No contact list data available to export.", { variant: "error" });
                    return;
                }

                const success = exportToCSV(
                    listsToExport,
                    contactListColumns,                    
                    getExportFilename(user?.email ?? "unknown", "ContactLists")
                );

                if (success) {
                    enqueueSnackbar("Contact lists exported successfully!", { variant: "success" });
                } else {
                    enqueueSnackbar("Failed to generate CSV file.", { variant: "error" });
                }
            } catch (err) {
                console.error("Export error:", err);
                enqueueSnackbar("Error while exporting contact lists.", { variant: "error" });
            } finally {
                setExportLoading(false);
            }
        };

        return (
            <Button
                startIcon={<Downloading />}
                variant="contained"
                color="primary"
                onClick={handleExportContactLists}
                disabled={exportLoading}
            >
                {exportLoading ? "Exporting..." : "Export"}
            </Button>
        );
    };

    const ImportCsvContactListButton = () => {
            return (
                <Stack direction="row" spacing={1}>
                    <Button startIcon={<Downloading />}
                        variant="contained"
                        color="primary"
                        onClick={() => {
                            setOpenCsvImportDialog(true);
                        }}>
                        Import
                    </Button>
                    <ExportContactListsCsvButton />
                </Stack>
            )
        }

    const columnCustomizerLabel = {
		email: "Name",
		created_on: "Imported On",
		contacts_count: "Total Contacts",
		column_count: "Total Columns",
		status: "Status",
		source: "Source",
	};

	// Columns for contact lists table.
	const columnDefs: ColDef[] = [
		{ field: "name", headerName: "Name", cellRenderer: ContactListDetailsLink },
		{
			field: "created_on",
			headerName: "Imported On",
			valueFormatter: (params) => timestampFormatter(params),
			flex: 2
		},
		{ field: "contacts_count", headerName: "Total Contacts" },
		{ field: "column_count", headerName: "Total Columns" },
		// { field: "status", headerName: "Status", cellRenderer: StatusBadgeCell, sortable: false },
		{ field: "source", headerName: "Source" },
        ...(pageData?.enable_contact_verification ? [{
            field: "email_verification_status",
            headerName: "Verification Status",
            cellRenderer: EmailVerificationStatusCell,
            sortable: true
        }] : []),
        { field: "workspace_id", headerName: "Workspace ID" },
        { field: "workspace_name", headerName: "Workspace Name" },
		{ headerName: "Actions", cellRenderer: ActionsButton, sortable: false, resizable: false },
	]

    // ========================================================================
    // --------------------------- MAIN RENDER CODE ---------------------------
    // ========================================================================

    if ( !pageData && pageDataQuery.isLoading) {
        return (
            <PageLoading/>
        )

    } else if (pageDataQuery.error as unknown as ApiRequestFailed) {
        return <PageDataErrorHandler error={pageDataQuery.error as unknown as ApiRequestFailed}/>

    } else if (pageData) {
        return (
            <Stack direction={"column"} spacing={4} sx={{pb: 4, height: "100%"}}>
                <Stack direction={"row"} spacing={2} alignItems={"center"}>
                    <IconButton
                        onClick={() => navigate(urls["adminUserDetails"].replace(":userId", userId || ""))}><ArrowBack/></IconButton>
                    <Typography fontWeight={"bold"} variant={"h5"}>Contact List ({pageData.user_email})</Typography>
                </Stack>
                <Divider/>
                <Box sx={{ height: "100%", width: "100%", overflow: "hidden" }}>
                    <PaginatedServerSideDataTable
                        columns={columnDefs}
                        paginationData={pageData}
                        noRowsText={"No Contact List Available"}
                        loading={pageDataQuery.isFetching}
                        onPageChange={handlePageChange}
                        onPageSizeChange={handlePageSizeChange}
                        onSearchChange={handleSearchChange}
                        searchPlaceholder="Search name, status or source..."
                        actions={ImportCsvContactListButton()}
                        columnLabels={columnCustomizerLabel}
                    />
                </Box>
                <ImportContactListDialog open={openCsvImportDialog}
                    userId={userId!}
					taskId={taskId}
                    workspaceDetails={pageData.workspace_details ?? []}
                    contactVerification={pageData.enable_contact_verification}                    
                    workspace={workspace}
                    setWorkspaces={setWorkspaces}
					setTaskId={setTaskId}
					checkContactListId={checkContactListId}
					setCheckContactListId={setCheckContactListId}
					isAdmin={isAdmin}
					close={closeCsvImportDialog}
					success={csvContactListImportSuccessful} />									                       
            </Stack>
        )

    } else {
        // Ideally it should not reach here.
        return <></>
    }
}


function ImportContactListDialog(props: {
    userId: string,
    open: boolean,
    close: () => void,
    workspaceDetails: WorkspaceDetail[],
    contactVerification: boolean
    workspace: WorkspaceDetail | null,
    setWorkspaces: React.Dispatch<React.SetStateAction<WorkspaceDetail | null>>,
    success: () => void,
    checkContactListId: string;    
    isAdmin: boolean;
    setCheckContactListId: (id: string) => void;
    taskId: string;
    setTaskId: (id: string) => void;
}) {
    const { enqueueSnackbar } = useSnackbar();

    const [isVerificationDialogOpen, setIsVerificationDialogOpen] = useState<boolean>(false);

    // Core States
    const [listName, setListName] = useState<string>("");
    const [source, setSource] = useState<string>("");

    // CSV Specific States
    const [file, setFile] = useState<File | null>(null);
    const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
    const [contactsData, setContactsData] = useState<string[][]>([]);
    const [fieldMappings, setFieldMappings] = useState<FieldMappingType[]>([]);
    const [hasUnsubscribeColumn, setHasUnsubscribeColumn] = useState<boolean>(false);
    const [unsubscribeColumnMapping, setUnsubscribeColumnMapping] = useState<string>("");
    const [showFieldMapping, setShowFieldMapping] = useState<boolean>(false);    
    const [isSubmitAttempted, setIsSubmitAttempted] = useState(false);

    // HubSpot Specific States
    const [hubspotFields, setHubspotFields] = useState<string[]>([]);
	const [showHubspotMapping, setShowHubspotMapping] = useState(false);
    const [hubspotListId, setHubspotListId] = useState<string>("");

    // Google Sheets Specific States
    const [googleSheetsUrl, setGoogleSheetsUrl] = useState<string>("");
    const [googleSheetsFields, setGoogleSheetsFields] = useState<any[]>([]);    
	const [showGoogleSheetsMapping, setShowGoogleSheetsMapping] = useState<boolean>(false);
    const [isEmailColFilled, setIsEmailColFilled] = useState<boolean>(false);

    useEffect(() => {
        if (!props.open) {
            // Core States
            setListName("");
            setSource("");
            setIsSubmitAttempted(false);
            setIsVerificationDialogOpen(false);

            // CSV Specific States
            setFile(null);
            setCsvHeaders([]);
            setContactsData([]);
            setFieldMappings([]);
            setHasUnsubscribeColumn(false);
            setUnsubscribeColumnMapping("");
            setShowFieldMapping(false);

            // HubSpot Specific States
            setHubspotFields([]);
            setShowHubspotMapping(false);
            setHubspotListId("");
            hubspotFieldsQuery.reset();

            // Google Sheets Specific States
            setGoogleSheetsUrl("");
            setGoogleSheetsFields([]);
            setShowGoogleSheetsMapping(false);
            setIsEmailColFilled(false);
            googleSheetsFieldsQuery.reset();

            // Reset Mutations
            addContactListMutation.reset();
            addHubspotContactListMutation.reset();
            addGoogleSheetsContactListMutation.reset();
        }
    }, [props.open]);

    const hubspotFieldsQuery = useMutation({
		mutationKey: ["AdminHubspotFieldsQuery"],		
        mutationFn: (data: {workspace_id: number | undefined}) => authenticateAndPostData("/admin/hubspot/get-contact-fields/", {            
            workspace_id: data.workspace_id,
        }),
		onSuccess: (res) => {
			setHubspotFields(res.data.fields);
			setShowHubspotMapping(true);
		},
		onError: () => {
            enqueueSnackbar("Failed to fetch HubSpot fields", {
                    variant: "error",
                });			
		},
	});

	const googleSheetsFieldsQuery = useMutation({
		mutationKey: ["AdminGoogleSheetsFieldsQuery"],
		mutationFn: () => authenticateAndPostData("/admin/google-sheets/get-contact-fields/", {
			sheets_url: googleSheetsUrl,
		}),
		onSuccess: (res) => {
			setGoogleSheetsFields(res.data.data);
			setShowGoogleSheetsMapping(true);
			if (!res.data.success) {
                enqueueSnackbar("Unable to fetch fields from the provided Google Sheets URL", {
                    variant: "error",
                });				
			} else {
                enqueueSnackbar("Google Sheets fields fetched successfully.", {
                    variant: "success",
                });				
			}
		},
		onError: () => {
            enqueueSnackbar("Failed to fetch Google Sheets fields", {
                    variant: "error",
                });				
		},
	});

	useEffect(() => {
		if (source === "Hubspot" && hubspotFields.length === 0 && !hubspotFieldsQuery.isPending)
			hubspotFieldsQuery.mutate({ workspace_id: props.workspace?.id });
		if (source === "Google Sheets" && googleSheetsUrl && googleSheetsFields.length === 0 && !googleSheetsFieldsQuery.isPending)
			googleSheetsFieldsQuery.mutate();
	}, [source]);

	useEffect(() => {
		if (source !== "Hubspot") {
			setHubspotListId("");
			setShowHubspotMapping(false);
			setHubspotFields([]);
		}
		if (source !== "Google Sheets") {
			setGoogleSheetsUrl("");
			setShowGoogleSheetsMapping(false);
			setGoogleSheetsFields([]);
		}
		if (source !== "CSV") {
			setFile(null);
			setCsvHeaders([]);
			setContactsData([]);
			setShowFieldMapping(false);
		}
		setFieldMappings([]);
		setHasUnsubscribeColumn(false);
		setUnsubscribeColumnMapping("");
	}, [source]);

	useEffect(() => {
		setIsSubmitAttempted(false);
	}, [source, hubspotListId, googleSheetsUrl, file]);

    // Mutations
    const addContactListMutation = useMutation({
        mutationKey: ["AdminAddContactListMutation"],
        mutationFn: () => {
            return authenticateAndPostData("/admin/add-contact-list/", {
                name: listName.trim(),
                user_id: props.userId,
                workspace_id: props.workspace?.id,
                csv_data: contactsData,
                csv_headers: csvHeaders,
                field_mappings: fieldMappings,
                has_unsubscribe_column: hasUnsubscribeColumn,
                unsubscribe_column_mapping: unsubscribeColumnMapping,
            });
        },
        onSuccess: () => {
            enqueueSnackbar("Success! Your new contact list is being created.", {
                variant: "success",
            });
            props.success();
        },
        onError: (error: ApiRequestFailed) => {
            enqueueSnackbar(error.data.message, { variant: "error" });
        },
    });    

    const addHubspotContactListMutation = useMutation({
		mutationKey: ["AdminAddHubspotContactListMutation"],
		mutationFn: () => authenticateAndPostData("/admin/hubspot/create-contact-list-with-mapping/", {
			name: listName.trim(),
			hubspot_list_id: hubspotListId,
			field_mappings: fieldMappings,            
            workspace_id: props.workspace?.id,
		}),
		gcTime: 0,
		retry: retryFn,
		onSuccess: () => {			
            enqueueSnackbar("Success! Your new hubspot contact list is being created.", {
                    variant: "success",
                });
            props.success();
		},
		onError: (error: ApiRequestFailed) => {			
            enqueueSnackbar(error.data.message, {
                    variant: "error",
                });
		},
	});

	const addGoogleSheetsContactListMutation = useMutation({
		mutationKey: ["AdminAddGoogleSheetsContactListMutation"],
		mutationFn: () => authenticateAndPostData("/admin/google-sheets/create-contact-list-with-mapping/", {
			name: listName.trim(),
			sheets_url: googleSheetsUrl,
			field_mappings: fieldMappings,            
            workspace_id: props.workspace?.id,
		}),
		gcTime: 0,
		retry: retryFn,
		onSuccess: () => {			
            enqueueSnackbar("Success! Your new Google Sheets contact list is being created.", {
                    variant: "success",
                });
            props.success();    	
		},
		onError: (error: ApiRequestFailed) => {
			enqueueSnackbar(error.data.message, {
                    variant: "error",
                });
		},
	});

    // Cleanup states on source conversion
    useEffect(() => {
        setFile(null);
        setCsvHeaders([]);
        setContactsData([]);
        setFieldMappings([]);
        setShowFieldMapping(false);
        setHubspotListId("");
        setGoogleSheetsUrl("");
    }, [source]);

    // Parse CSV file Hook
    useEffect(() => {
        if (file) {
            file.text().then(value => {
                let parseResult = Papa.parse(value, {
                    skipEmptyLines: "greedy",
                });
                let csvData = parseResult.data as Array<Array<string>>;
                csvData = csvData.filter(value => value.length);

                setCsvHeaders(csvData[0]);
                setContactsData(csvData.slice(1));
                setShowFieldMapping(true);
            });
        }
    }, [file]);

    const VisuallyHiddenInput = styled('input')({
        clip: 'rect(0 0 0 0)',
        clipPath: 'inset(50%)',
        height: 1,
        overflow: 'hidden',
        position: 'absolute',
        bottom: 0,
        left: 0,
        whiteSpace: 'nowrap',
        width: 1,
    });

    function onFileSelected(event: ChangeEvent<HTMLInputElement>) {
        if (event.target.files && event.target.files[0]) {
            setFile(event.target.files[0]);
        }
    }

    const handleFieldMappingsChange = (mappings: FieldMappingType[]) => {
        setFieldMappings(mappings);
    };

    const handleUnsubscribeChange = (hasUnsubscribe: boolean, columnMapping?: string) => {
        setHasUnsubscribeColumn(hasUnsubscribe);
        setUnsubscribeColumnMapping(columnMapping || "");
    };

    function confirmButtonHandler() {
        if (!props.workspace?.id) {
            enqueueSnackbar("Workspace not found.", { variant: "error" });
            return;
        }

        if (!listName.trim()) {
            enqueueSnackbar("List name is required.", { variant: "error" });
            return;
        }

        if (source === "Hubspot") {
            addHubspotContactListMutation.mutate();
        } else if (source === "Google Sheets") {
            // Adjust integration endpoint schema for Google Sheets logic mapping if needed
            addGoogleSheetsContactListMutation.mutate();
        } else {
            addContactListMutation.mutate();
        }
    }

    const isEmailMapped = () => {
        return fieldMappings.some(m => m.mappedField === 'Email Address' && m.csvColumn);
    };

    const confirmDisabled =
        listName.trim() === "" ||
        (source === "Hubspot"
            ? !hubspotListId || addHubspotContactListMutation.isPending
            : source === "Google Sheets"
                ? !googleSheetsUrl || googleSheetsFieldsQuery.isPending || addGoogleSheetsContactListMutation.isPending || (showFieldMapping && !isEmailMapped())
                : file === null || addContactListMutation.isPending || (showFieldMapping && !isEmailMapped())
        );

    return (
        <>
        <Dialog open={props.open} fullWidth maxWidth="md">
            <DialogTitle>Import Contact List</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    Upload a CSV with maximum 100,000 rows.
                </DialogContentText>
                <Stack direction={"column"} spacing={1} sx={{ mt: 2 }}>                    
                    <Typography variant={"subtitle1"} sx={{ fontWeight: "bold" }}>
                        Select workspace to add new contact list:
                    </Typography>                                     
                    <Autocomplete
                        options={props.workspaceDetails}
                        value={props.workspace}
                        onChange={(_, newValue) => props.setWorkspaces(newValue)}
                        getOptionLabel={(option) => option.name}
                        renderInput={(params) => <TextField {...params} label="Workspace" />}        
                        PopperComponent={(popperProps) => (
                            <Popper {...popperProps} style={{ zIndex: 2000 }} />
                        )}
                    />

                    <Typography variant={"subtitle1"} sx={{ fontWeight: "bold" }}>
                        Contact List Name:
                    </Typography>
                    <TextField 
                        value={listName}
                        onChange={e => setListName(e.target.value)}
                        variant="outlined"
                        fullWidth
                        required 
                    />

                    <Typography variant={"subtitle1"} sx={{ fontWeight: "bold" }}>
                        Source:
                    </Typography>
                    <FormControl fullWidth variant="outlined">
                        <Select
                            id="source-select"
                            value={source}
                            onChange={(e) => setSource(e.target.value)}                            
                        >                            
                            <MenuItem value="" disabled>
                                <em>Select an integration source</em>
                            </MenuItem>
                            <MenuItem value="CSV">CSV File</MenuItem>
                            <MenuItem value="Google Sheets">Google Sheets</MenuItem>
                            <MenuItem value="Hubspot">HubSpot API</MenuItem>
                        </Select>
                    </FormControl>

                    {/* Conditional Source Modules Rendering */}
                    {source === "Hubspot" && (
                        <HubspotContact
                            hubspotListId={hubspotListId}
                            setHubspotListId={setHubspotListId}
                            workspace_id={props.workspace?.id}
                        />
                    )}

                    {source === "Google Sheets" && (
                        <Stack spacing={2} sx={{ mt: 1, p: 2, border: '1px solid #ccc', borderRadius: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                Connect Google Sheets
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Connect your spreadsheet and map target parameters safely.
                            </Typography>
                            <Stack direction="row" spacing={1}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Google Sheets URL"
                                    value={googleSheetsUrl}
                                    onChange={(e) => {
                                        setGoogleSheetsUrl(e.target.value);
                                        setGoogleSheetsFields([]);
                                        setIsEmailColFilled(false);
                                    }}
                                    placeholder="Paste shared Google Sheets URL string link"
                                />
                                <Button
                                    variant="contained"
                                    size="small"
                                    disabled={googleSheetsFieldsQuery.isPending || !googleSheetsUrl}
                                    onClick={() => googleSheetsFieldsQuery.mutate()}
                                >
                                    {googleSheetsFieldsQuery.isPending ? "Validating..." : "Validate"}
                                </Button>
                            </Stack>
                        </Stack>
                    )}

                    {source === "CSV" && (
                        <Stack spacing={1} sx={{ mt: 1 }}>
                            <DialogContentText variant="body2">
                                Upload a standard text CSV containing a maximum limit of 100,000 records.
                            </DialogContentText>
                            <Button
                                component="label"
                                variant="contained"
                                color="primary"
                                disabled={addContactListMutation.isPending}
                                startIcon={<CloudUpload />}
                                sx={{ width: 'fit-content' }}
                            >
                                Upload .CSV File
                                <VisuallyHiddenInput
                                    type="file"
                                    accept=".csv"
                                    onChange={onFileSelected}
                                />
                            </Button>
                            <Typography variant="subtitle2" sx={{ mt: 1 }}>
                                {file ? `Selected File: ${file.name}` : "No Files Uploaded"}
                            </Typography>
                        </Stack>
                    )}

                    {/* Shared Mapping Components for Spreadsheet Matrices (CSV / Google Sheets Mapping definitions) */}
                    {((source === "CSV" && file && showFieldMapping && csvHeaders.length > 0) ||
                        (source === "Hubspot" && hubspotListId && showHubspotMapping && hubspotFields.length > 0) ||
                        (source === "Google Sheets" && showGoogleSheetsMapping && googleSheetsFields?.length > 0)) && (
                        <>                            
                            <div>
                                <h3 className="text-lg font-semibold">Match Your Data</h3>
                                <p className="text-muted-foreground text-sm">
                                    {source === "Hubspot" 
                                        ? "Map properties from HubSpot to your platform fields." 
                                        : source === "Google Sheets" 
                                        ? "Map columns from your Google Sheet to your platform fields." 
                                        : "Map columns from your uploaded CSV file."
                                    }
                                </p>
                            </div>
                            
                            <div className="w-full h-full pr-2">
                                <AdminFieldMapping
                                    workspaceId={props.workspace?.id}
                                    source={
                                        source === "Hubspot" 
                                            ? "hubspot" 
                                            : source === "Google Sheets" 
                                            ? "google sheets" 
                                            : "csv"
                                    }
                                    sourceHeaders={
                                        source === "Hubspot" 
                                            ? hubspotFields 
                                            : source === "Google Sheets" 
                                            ? googleSheetsFields
                                            : csvHeaders
                                    }
                                    csvData={source === "CSV" ? contactsData : []}
                                    initialMappings={fieldMappings}
                                    hasUnsubscribeColumn={hasUnsubscribeColumn}
                                    unsubscribeColumnMapping={unsubscribeColumnMapping}
                                    onMappingsChange={handleFieldMappingsChange}
                                    onUnsubscribeChange={handleUnsubscribeChange}
                                    integrationTypeId={
                                        source === "Hubspot" 
                                            ? hubspotListId 
                                            : source === "Google Sheets" 
                                            ? googleSheetsUrl 
                                            : ""
                                    }
                                    setIsEmailColFilled={setIsEmailColFilled}
                                    isEmailColFilled={isEmailColFilled}
                                    isSubmitAttempted={addContactListMutation.isPending || addHubspotContactListMutation.isPending}
                                />
                            </div>
                        </>
                    )}                

                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={props.close} disabled={addContactListMutation.isPending || addHubspotContactListMutation.isPending}>
                    Cancel
                </Button>
               <Button 
                    disabled={confirmDisabled}
                    className="cursor-pointer"
                    onClick={() => {
                        setIsSubmitAttempted(true);
                        if (source !== "CSV" && !isEmailColFilled) {
                            setTimeout(() => {
                                document.getElementById("email-error")?.scrollIntoView({
                                    behavior: "smooth",
                                    block: "center",
                                });
                            }, 100);
                            return;
                        }
                        
                        if (props.contactVerification) {
                                setIsVerificationDialogOpen(true);
                        } else {
                            confirmButtonHandler();
                        }
                    }}>
                    {source === "Hubspot"
                        ? addHubspotContactListMutation.isPending
                            ? "Creating..."
                            : "Create this Contact List"
                        : source === "Google Sheets"
                            ? addGoogleSheetsContactListMutation.isPending
                                ? "Creating..."
                                : "Create this Contact List"
                            : addContactListMutation.isPending
                                ? "Creating.."
                                : "Create this Contact List"}
                </Button>
            </DialogActions>
        </Dialog>
        <Dialog open={isVerificationDialogOpen} onClose={() => setIsVerificationDialogOpen(false)} style={{ zIndex: 2100 }}>
                <DialogContent>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>Verify Email List</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Do you want to verify this email list?<br/><br/>
                        Verification can take anywhere between <strong>20 minutes to 3 hours or longer</strong>, depending on the list size.
                        <br/><br/>
                        You can continue creating campaigns using this list, but you will not be able to <strong>start/schedule the campaigns until verification is complete</strong>.
                    </Typography>
                    <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 3 }}>
                        <Button
                            variant="outlined"
                            onClick={() => {
                                setIsVerificationDialogOpen(false);
                                // If "No", go ahead and build without explicit validation flag setup if supported
                                confirmButtonHandler(); 
                            }}
                        >
                            No
                        </Button>
                        <Button
                            variant="contained"
                            onClick={() => {
                                setIsVerificationDialogOpen(false);
                                confirmButtonHandler();
                            }}
                        >
                            Yes, Proceed
                        </Button>
                    </Stack>
                </DialogContent>
            </Dialog>
        </>
        
    );
}

function HubspotContact({
    hubspotListId,
    setHubspotListId,
    workspace_id,
}: {
    hubspotListId: string;
    setHubspotListId: (id: string) => void;
    workspace_id?: number;
}) {
    const [hubspotLists, setHubspotLists] = useState<any[]>([]);
    const [importMode, setImportMode] = useState<"all" | "segments">("all");

    const pageDataQuery = useQuery({
        queryKey: ["hubspotList"],
        queryFn: () => authenticateAndFetchData(`/admin/hubspot/get-hubspot-lists/?workspace_id=${workspace_id}`),
        retry: retryFn,
        refetchOnWindowFocus: false,
    });

    useEffect(() => {
        if (pageDataQuery.data) {
            const hubspotLists = pageDataQuery.data.data.lists as any[];
            setHubspotLists(hubspotLists || []);
        }
    }, [pageDataQuery.data]);

    useEffect(() => {
        if (importMode === "all" && hubspotListId !== "0") {
            setHubspotListId("0");
        }
    }, [importMode, hubspotListId, setHubspotListId]);

    if (pageDataQuery.isLoading) {
        return (
            <Stack
                alignItems="center"
                justifyContent="center"
                sx={{ mt: 3, mb: 2 }}
            >
                <l-mirage size="120" color={"#42a5f5"}></l-mirage>
            </Stack>
        );
    }
    if (!hubspotLists.length) {
        return (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                No List is available on Hubspot.
            </Typography>
        );
    }
    return (
        <Stack spacing={2} sx={{ mt: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                Import Options:
            </Typography>

            {/* Radio Buttons for Mode */}
            <RadioGroup
                value={importMode}
                onChange={(e) => {
                    const mode = e.target.value as "all" | "segments";
                    setImportMode(mode);

                    // Reset hubspotListId when switching
                    if (mode === "all") {
                        setHubspotListId("0");
                    } else {
                        setHubspotListId("");
                    }
                }}
            >
                <FormControlLabel
                    value="all"
                    control={<Radio />}
                    label="Import all contacts from HubSpot"
                />
                <FormControlLabel
                    value="segments"
                    control={<Radio />}
                    label="Import from Segments (Lists)"
                />
            </RadioGroup>
            {importMode === "segments" && hubspotLists.length > 0 && (
                <>
                    <Typography variant="body2" color="text.secondary">
                        Select a list from HubSpot:
                    </Typography>
                    <FormControl fullWidth>
                        <Select
                            value={hubspotListId}
                            onChange={(e) => setHubspotListId(e.target.value)}
                        >
                            {hubspotLists.map((list: any) => (
                                <MenuItem key={list.listId} value={list.listId}>
                                    {list.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </>
            )}

            {importMode === "segments" && hubspotLists.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                    No segment lists available in HubSpot. Please create one first.
                </Typography>
            )}
        </Stack>
    );
}

function HubspotColumnSelection(props: {
    open: boolean;
    availableColumns: string[];
    checkContactListId: string;
    close: () => void;
    success: () => void;
}) {
    const [selected, setSelected] = useState<string[]>([]);

    const toggleColumn = (col: string) => {
        setSelected(prev =>
            prev.includes(col) ? prev.filter(c => c !== col) : [...prev, col]
        );
    };

    // Mutation to POST user-selected columns
    const saveSelectionMutation = useMutation({
        mutationKey: ["saveHubspotColumnSelection"],
        mutationFn: () => authenticateAndPostData("/hubspot/save-column-selection/", {
            contact_list_id: props.checkContactListId,
            selected_columns: selected,
        }),
        onSuccess: () => {
            props.close();
        },
        onError: (err: ApiRequestFailed) => {
            console.error(err);
        }
    });

    return (
        <Dialog open={props.open} onClose={props.close} fullWidth>
            <DialogTitle>Select Columns to Import</DialogTitle>
            <DialogContent>
                <FormGroup>
                    {props.availableColumns
                        ?.slice()
                        .sort((a, b) => a.localeCompare(b)) // A-Z sort
                        .map((col) => (
                            <FormControlLabel
                                key={col}
                                control={
                                    <Checkbox
                                        checked={selected.includes(col)}
                                        onChange={() => toggleColumn(col)}
                                    />
                                }
                                label={col}
                            />
                        ))}
                </FormGroup>
            </DialogContent>
            <DialogActions>
                <Button onClick={props.close}>Cancel</Button>
                <Button
                    onClick={() => saveSelectionMutation.mutate()}
                    disabled={!selected.length || saveSelectionMutation.isPending}
                >
                    {saveSelectionMutation.isPending ? 'Fetching Columns...' : 'Import Selected'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function DeleteContactList(props: {
    open: boolean;
    close: () => void;
    contactName: string;
    onConfirm: () => void;
    isPending: boolean;
}) {
    return (
        <Dialog 
            open={props.open} 
            onClose={props.close}            
            maxWidth="xs" 
            fullWidth
        >
            {/* Header */}
            <DialogTitle sx={{ fontWeight: "600", fontSize: "1.125rem" }}>
                Delete Contact List
            </DialogTitle>
            
            {/* Content Body & Description */}
            <DialogContent>
                <DialogContentText>
                    Are you sure you want to delete this <span className="font-medium">{props.contactName}</span>? This won't
                    affect any running campaigns,
                    but you will lose all stats generated in this list.
                </DialogContentText>
            </DialogContent>
            
            {/* Footer Actions */}
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button 
                    variant="outlined" 
                    onClick={props.close} 
                    disabled={props.isPending}
                >
                    Cancel
                </Button>
                <Button 
                    variant="contained" 
                    color="error" 
                    onClick={props.onConfirm} 
                    disabled={props.isPending}
                >
                    {props.isPending ? "Deleting..." : "Delete"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}